import {
    IAFSmartAvailableAnswer,
    IAnswerProps,
    IAppFormQuestion
} from "../interfaces/IAppFormData";
import { IDropdownOption } from "../../common/interfaces/IDropdownOption";
import { InsurerList } from "../enums/InsurerList";
import { useHistory } from "react-router-dom";
import { format, isValid } from "date-fns";

export const getQuestionType = (question: IAppFormQuestion): string => {
    return question?.questionType || "";
};

export const checkInputDate = (question: IAppFormQuestion): boolean => {
    const isForDateInput = question?.controlTypeName === "DateOfBirth";
    return !!isForDateInput || false;
};

export const getQuestionLabel = (question: IAppFormQuestion): string => {
    return question?.disclosureName || question?.questionHeaderText || "";
};

export const getQuestionHeaderText = (question: IAppFormQuestion): string => {
    return question?.disclosureName ? question?.questionHeaderText || "" : "";
};

export const needFieldBorders = (question: IAppFormQuestion): boolean => {
    return (
        question?.controlTypeName === "Date" ||
        (question?.controlTypeName === "Number" && !!question?.unit) ||
        false
    );
};

export const getControlType = (question: IAppFormQuestion): number => {
    return question?.controlTypeId || 0;
};

export const checkInValidAnswer = (answer: string): boolean => {
    return !answer || answer === "Invalid date" || false;
};

export const getDisclosureAnswersValue = (
    availableAnswers: IAFSmartAvailableAnswer[],
    cantFind: boolean
): string => {
    const cantFindString = "CantFind";
    if (cantFind) return cantFindString;
    return availableAnswers.find((i) => i.value !== cantFindString).value;
};

export const getYearListOptions = (
    questionParameter: string,
    isNotSureOptionAvailable: boolean
): Array<any> => {
    const year = new Date().getFullYear();
    let lastOptionYear;
    const daysOfAYear = 365;
    const listCount = questionParameter ? parseInt(questionParameter) / daysOfAYear : 10;
    const options = Array.from(new Array(listCount + 1), (val, index) => {
        lastOptionYear = year - index;
        return {
            label: `${year - index}`,
            value: year - index
        };
    });

    return [
        ...options,
        {
            label: `before ${lastOptionYear}`,
            value: `P,${lastOptionYear - 1}`
        }
    ];
};

export const checkDateNotSure = (
    availableQuestionAnswers: IAFSmartAvailableAnswer[]
): boolean => {
    return availableQuestionAnswers?.length > 1;
};

const MAX_YEAR_TO_SHOW_MONTHS = 3;
export const checkYearForExtraInput = (value): boolean => {
    const currentDate = new Date();
    const diffDate = currentDate.getFullYear() - new Date(value).getFullYear();
    return diffDate < MAX_YEAR_TO_SHOW_MONTHS;
};

export const getMonthListOptions = (): Array<any> => {
    return [
        {
            label: "January",
            value: "0"
        },
        {
            label: "February",
            value: "1"
        },
        {
            label: "March",
            value: "2"
        },
        {
            label: "April",
            value: "3"
        },
        {
            label: "May",
            value: "4"
        },
        {
            label: "June",
            value: "5"
        },
        {
            label: "July",
            value: "6"
        },
        {
            label: "August",
            value: "7"
        },
        {
            label: "September",
            value: "8"
        },
        {
            label: "October",
            value: "9"
        },
        {
            label: "November",
            value: "10"
        },
        {
            label: "December",
            value: "11"
        }
    ];
};

export const generateAnswerList = (questionList): Array<IAnswerProps> => {
    return questionList.reduce((acc, { id, disclosures, answer }) => {
        return [
            ...acc,
            {
                questionId: id,
                disclosure: disclosures,
                answer: answer
            }
        ];
    }, []);
};

export const checkDateValueValid = (dateString): boolean => {
    return isValid(dateString);
};

export const showHelpText = (question: IAppFormQuestion): string => {
    if (!question?.helpText?.length) {
        return "";
    }
    const helpText = question?.helpText?.reduce((acc, item) => {
        if (item?.helpText?.length) {
            acc = `${item?.helpText[0].text}`;
        }
        return acc;
    }, "");
    return helpText;
};

export const getQuestionID = (question): number => {
    return question?.id || 0;
};

export const accountNumberFormat = (value) => {
    var v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    var matches = v.match(/\d{4,8}/g);
    var match = (matches && matches[0]) || "";
    var parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
        parts.push(match.substring(i, i + 4));
    }
    if (parts.length > 1 && parts[1].length > 4) {
        parts[1] = [parts[1].slice(0, 4), " ", parts[1].slice(4)].join("");
    }
    if (parts.length) {
        return parts.join(" - ");
    } else {
        return v;
    }
};

export const bsbNumberFormat = (value) => {
    var val = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    if (val?.length > 4) {
        return val.slice(0, 4);
    }
    return val;
};

export const createRange = (start: number, end: number): Array<number> => {
    const res = [];
    for (let i = start; i <= end; i++) {
        res.push(i);
    }
    return res;
};

export const getOptions = (start: number, end: number): IDropdownOption[] => {
    return createRange(start, end).map((i) => ({
        value: i.toString(),
        label: i.toString()
    }));
};

export const getInsurerName = (providerId: number): string => {
    switch (providerId) {
        case InsurerList.AIA:
            return "AIA";
        case InsurerList.FIDELITY:
            return "Fidelity Life";
        case InsurerList.ASTERON:
            return "Asteron Life";
        case InsurerList.NIB:
            return "nib";
        case InsurerList.PARTNERS_LIFE:
            return "Partners Life";
        case InsurerList.CHUBB_LIFE:
            return "Chubb Life";
        case InsurerList.SOUTHERN_CROSS:
            return "Southern Cross";
        case InsurerList.ACCURO:
            return "Accuro";
        case InsurerList.CHUBB_LIFE_OLD:
            return "Chubb Life";
        default:
            return "";
    }
};

export const isNumber = (value) => {
    return typeof value === "number";
};

export const redirectTo404Page = () => {
    window.location.href = "/error/404.htm";
};

export const isDateInvalid = (date: string): boolean => {
    const datePicked = new Date(date);
    var diff_ms = Date.now() - datePicked.getTime();
    if (diff_ms < 0) {
        return true;
    }
    return false;
};

export const decodeHtml = (html) => {
    var txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
};

export const bankAccountValidate = (account): boolean => {
    return (String(account).length >= 14 && String(account).length <= 16) || false;
};

export const formatDate = (date, formatStr = "yyyy-MM-dd"): string => {
    return format(date, formatStr);
};
